import { isClickedOutside } from '@theme/utilities';

class DropdownGroupComponent extends HTMLElement {
  constructor() {
    super();

    this.dropdowns = [];
  }

  connectedCallback() {
    this.dropdowns = this.querySelectorAll('dropdown-component');

    document.addEventListener('click', this.#handleDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  #handleDocumentClick = (event) => {
    const clickedInsideDropdown = event.target.closest('dropdown-component');
    if (!clickedInsideDropdown) {
      this.closeAllDropdowns();
    }
  };

  closeAllDropdowns() {
    this.dropdowns.forEach((dropdown) => {
      dropdown.close();
    });
  }
}

if (!customElements.get('dropdown-group-component')) {
  customElements.define('dropdown-group-component', DropdownGroupComponent);
}

class DropdownComponent extends HTMLElement {
  constructor() {
    super();

    this.toggle = null;
    this.content = null;
    this.moreToggle = null;
    this.closeButton = null;
    this.isOpen = false;
  }

  connectedCallback() {
    this.toggle = this.querySelector('[data-ref="dropdown-toggle"]');
    this.content = this.querySelector('[data-ref="dropdown-content"]');
    this.moreToggle = this.querySelector('[data-ref="dropdown-more-toggle"]');
    this.closeButton = this.querySelector('[data-ref="dropdown-close"]');
    if (this.toggle && this.content) {
      this.content.id ||= 'DropdownContent-' + Math.random().toString(36).slice(2);
      this.toggle.setAttribute('aria-controls', this.content.id);
      this.toggle.setAttribute('aria-expanded', 'false');
    }

    this.toggle?.addEventListener('click', this.#handleToggle);
    this.moreToggle?.addEventListener('click', this.#handleMoreToggle);
    this.closeButton?.addEventListener('click', this.#handleCloseButton);
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.#handleToggle);
    this.moreToggle?.removeEventListener('click', this.#handleMoreToggle);
    this.closeButton?.removeEventListener('click', this.#handleCloseButton);
  }

  #handleToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.isOpen) {
      this.close();
    } else {
      const dropdownScope = this.closest('header-component') ?? this.closest('dropdown-group-component');

      dropdownScope?.querySelectorAll('dropdown-component').forEach((dropdown) => {
        dropdown.close();
      });
      this.open();
    }
  };

  #handleCloseButton = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.close();
    this.toggle?.focus({ preventScroll: true });
  };

  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.close();
    }
  };

  #handleMoreToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const expanded = this.moreToggle?.getAttribute('aria-expanded') === 'true';
    this.querySelectorAll('[data-mm-extra-link]').forEach((link) => {
      link.hidden = expanded;
    });
    this.querySelectorAll('[data-mm-preview-link]').forEach((preview) => {
      preview.hidden = !expanded;
    });
    this.moreToggle?.setAttribute('aria-expanded', String(!expanded));

    const label = this.moreToggle?.querySelector('[data-mm-more-label]');
    if (label) {
      label.textContent = expanded ? this.moreToggle.dataset.showLabel : this.moreToggle.dataset.hideLabel;
    }
  };

  #collapseExtraLinks() {
    if (!this.moreToggle) return;

    this.querySelectorAll('[data-mm-extra-link]').forEach((link) => {
      link.hidden = true;
    });
    this.querySelectorAll('[data-mm-preview-link]').forEach((preview) => {
      preview.hidden = false;
    });
    this.moreToggle.setAttribute('aria-expanded', 'false');

    const label = this.moreToggle.querySelector('[data-mm-more-label]');
    if (label) label.textContent = this.moreToggle.dataset.showLabel;
  }

  open() {
    this.setAttribute('data-open', 'true');
    this.toggle?.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.#handleClickOutside);
    this.isOpen = true;
  }

  close() {
    this.setAttribute('data-open', 'false');
    this.toggle?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.#handleClickOutside);
    this.#collapseExtraLinks();
    this.isOpen = false;
  }
}

if (!customElements.get('dropdown-component')) {
  customElements.define('dropdown-component', DropdownComponent);
}
